import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LogIn, Phone } from "lucide-react";
import Seo from "../components/Seo";
import { useAuth } from "../hooks/useAuth";
import { PasswordField } from "../components/PasswordField";
import { LOCATIONS } from "../lib/content";

const WB = LOCATIONS["west-branch"];

export default function AccountLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/account/trips";
  const [form, setForm] = useState({ email: params.get("email") || "", password: "" });
  const [state, setState] = useState({ busy: false, error: null });

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, error: null });
    try {
      await login(form.email.trim(), form.password);
      navigate(next);
    } catch (err) {
      setState({ busy: false, error: err.payload?.userMessage || err.message });
    }
  };

  return (
    <>
      <Seo title="Sign in | West Branch Marina" path="/account/login" noindex />
      <div className="mx-auto max-w-md px-5 py-28 lg:px-10">
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-navy">Sign in</h1>
        <p className="mt-3 text-sm text-slate-600">
          See your trips, balances and confirmation codes in one place.
        </p>

        <form data-testid="login-form" onSubmit={submit} className="mt-8 space-y-4" noValidate>
          {state.error && (
            <p data-testid="login-error" role="alert" aria-live="assertive" className="rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-800">
              {state.error}
            </p>
          )}
          <div>
            <label htmlFor="login-email" className="block text-xs font-bold uppercase tracking-wider text-slate-600">
              Email
            </label>
            <input
              id="login-email"
              data-testid="login-email"
              type="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 w-full rounded-xl border border-navy/15 px-4 py-3 text-sm focus:border-lake focus:outline-none focus:ring-2 focus:ring-lake/30"
            />
          </div>
          <PasswordField
            id="login-password"
            testId="login-password"
            label="Password"
            autoComplete="current-password"
            value={form.password}
            onChange={(v) => setForm({ ...form, password: v })}
          />
          <button
            type="submit"
            data-testid="login-submit"
            disabled={state.busy}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ember px-7 py-3.5 font-bold text-white hover:bg-ember-hover disabled:opacity-60"
          >
            <LogIn className="h-4 w-4" aria-hidden="true" />
            {state.busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-6 text-sm text-slate-600">
          New here?{" "}
          <Link to="/account/register" data-testid="login-register-link" className="font-bold text-lake underline">
            Create an account
          </Link>
          {" "}— or{" "}
          <Link to="/boat-rentals/west-branch" data-testid="login-guest-link" className="font-bold text-lake underline">
            book as a guest
          </Link>
          , no account needed.
        </p>

        {/* Self-service reset exists on the vendor API now, so we offer it. */}
        <p data-testid="login-password-help" className="mt-6 text-sm text-slate-600">
          <Link to="/account/forgot-password" data-testid="login-forgot-link" className="font-bold text-lake underline">
            Forgotten your password?
          </Link>{" "}
          We'll email you a reset link. Prefer to talk to someone? Call{" "}
          <a href={WB.phoneHref} className="inline-flex items-center gap-1 font-bold text-lake underline">
            <Phone className="h-3.5 w-3.5" aria-hidden="true" />
            {WB.phone}
          </a>
          .
        </p>
      </div>
    </>
  );
}
